// src/platform/OrgAnalyticsPanel.jsx
//
// Per-org analytics product entitlement management.
// Shows per-product pool summary, grant button, and grant history.
import { useEffect, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { productDisplayName } from "../constants/analyticsProducts";

export default function OrgAnalyticsPanel({ orgId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showGrant, setShowGrant] = useState(false);
    const [editGrant, setEditGrant] = useState(null);

    async function load() {
        if (!data) setLoading(true);
        setError(null);
        try {
            const res = await fetchWithAuth(`/api/v1/platform/orgs/${orgId}/analytics-grants`);
            if (!res.ok) throw new Error("Failed to load analytics grants");
            setData(await res.json());
        } catch (e) {
            console.error(e);
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, [orgId]);

    if (loading) {
        return (
            <div className="flex justify-center pt-6">
                <Loader2 className="animate-spin text-gray-400" size={20} />
            </div>
        );
    }

    if (error) return <div className="text-xs text-red-400">{error}</div>;
    if (!data) return null;

    const { product_summary = [], grants = [] } = data;

    return (
        <div className="space-y-4">
            {/* PRODUCT POOL SUMMARY */}
            <div className="rounded border border-bg-border bg-bg p-3 space-y-3">
                <div className="text-xs text-gray-300 font-semibold">Product Pools</div>

                {product_summary.length === 0 ? (
                    <div className="text-xs text-fg-secondary italic">
                        No active analytics grants.
                    </div>
                ) : (
                    product_summary.map(s => {
                        const consumed = Number(s.sq_mi_consumed) || 0;
                        const total = Number(s.sq_mi_purchased_total) || 0;
                        const pct = total > 0 ? (consumed / total) * 100 : 0;
                        const available = Math.max(0, total - consumed);
                        const isFlat = s.sq_mi_purchased_total === 0 && s.grant_count > 0;

                        return (
                            <div key={s.product_code}>
                                <div className="flex justify-between items-center text-xs">
                                    <span className="text-gray-400">
                                        {productDisplayName(s.product_code, s.display_name)}
                                        {s.is_bundle && (
                                            <span className="ml-2 text-accent-info text-[10px] uppercase">bundle</span>
                                        )}
                                        <span className="text-fg-secondary ml-2">
                                            ({s.grant_count} grant{s.grant_count !== 1 ? "s" : ""})
                                        </span>
                                    </span>
                                    <span className="text-gray-100">
                                        {isFlat ? (
                                            <span className="text-accent-info text-[10px] uppercase">active · flat-rate</span>
                                        ) : (
                                            <>
                                                {consumed.toFixed(1)} / {total.toFixed(1)} sq mi{" "}
                                                <span className="text-fg-secondary font-normal">
                                                    ({available.toFixed(1)} available)
                                                </span>
                                            </>
                                        )}
                                    </span>
                                </div>
                                {!isFlat && (
                                    <div className="mt-1 h-1.5 rounded bg-bg-soft">
                                        <div
                                            className={`h-1.5 rounded transition-[width] duration-200 ${pct >= 100 ? "bg-red-500"
                                                : pct >= 80 ? "bg-yellow-500"
                                                    : "bg-status-ok"
                                                }`}
                                            style={{ width: `${Math.min(pct, 100)}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* GRANT BUTTON */}
            <div>
                <button
                    onClick={() => setShowGrant(true)}
                    className="
                    flex items-center gap-2 px-3 py-1.5 rounded
                    bg-accent-action/20 border border-accent-action/60
                    text-accent-action hover:bg-accent-action/30 transition text-xs
                    "
                >
                    <Plus size={14} />
                    Grant Analytics
                </button>
            </div>

            {/* GRANTS TABLE */}
            <div>
                <div className="text-xs text-gray-300 font-semibold mb-2">
                    Analytics Grants
                </div>

                {grants.length === 0 ? (
                    <div className="text-xs text-fg-secondary italic">
                        No analytics grants.
                    </div>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="text-gray-400">
                            <tr>
                                <th className="text-left py-1">Product</th>
                                <th className="text-left py-1">AOI</th>
                                <th className="text-left py-1">Allocated</th>
                                <th className="text-left py-1">Consumed</th>
                                <th className="text-left py-1">Term</th>
                                <th className="text-left py-1">Contract</th>
                                <th className="text-left py-1">Status</th>
                                <th className="text-left py-1"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {grants.map(g => {
                                const termStart = new Date(g.term_start).toLocaleDateString();
                                const termEnd = g.term_end
                                    ? new Date(g.term_end).toLocaleDateString()
                                    : "—";

                                const statusColor = {
                                    active: "border-green-500/40 text-green-400",
                                    suspended: "border-yellow-500/40 text-yellow-400",
                                    revoked: "border-red-500/40 text-red-400",
                                    expired: "border-gray-500/40 text-gray-400",
                                }[g.status] || "border-gray-500/40 text-gray-400";

                                return (
                                    <tr key={g.id} className="border-t border-bg-border">
                                        <td className="py-1.5">
                                            {productDisplayName(g.product_code, g.product_display_name)}
                                            {g.is_bundle && (
                                                <span className="ml-1 text-accent-info text-[10px] uppercase">bundle</span>
                                            )}
                                        </td>
                                        <td className="py-1.5 text-gray-400">
                                            {g.aoi_name || "—"}
                                        </td>
                                        <td className="py-1.5">
                                            {g.sq_mi_purchased != null
                                                ? `${Number(g.sq_mi_purchased).toFixed(1)} sq mi`
                                                : "unmetered"}
                                        </td>
                                        <td className="py-1.5 text-gray-300">
                                            {Number(g.sq_mi_consumed).toFixed(1)}
                                        </td>
                                        <td className="py-1.5 text-gray-400">
                                            {termStart} – {termEnd}
                                        </td>
                                        <td className="py-1.5 text-gray-400">
                                            {g.contract_ref || "—"}
                                        </td>
                                        <td className="py-1.5">
                                            <span className={`px-2 py-0.5 rounded border ${statusColor}`}>
                                                {g.status}
                                            </span>
                                        </td>
                                        <td className="py-1.5 text-right">
                                            <button
                                                onClick={() => setEditGrant(g)}
                                                className="text-gray-400 hover:text-gray-200"
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* GRANT MODAL */}
            {showGrant && (
                <GrantAnalyticsModal
                    orgId={orgId}
                    onClose={() => setShowGrant(false)}
                    onRefresh={load}
                    onSuccess={async () => {
                        setShowGrant(false);
                        await load();
                    }}
                />
            )}

            {/* EDIT MODAL */}
            {editGrant && (
                <EditAnalyticsGrantModal
                    grant={editGrant}
                    onClose={() => setEditGrant(null)}
                    onSuccess={async () => {
                        setEditGrant(null);
                        await load();
                    }}
                />
            )}
        </div>
    );
}

// ──────────────────────────────────────────────────────────
// GrantAnalyticsModal
// ──────────────────────────────────────────────────────────
function GrantAnalyticsModal({ orgId, onClose, onRefresh, onSuccess }) {
    const today = new Date().toISOString().slice(0, 10);
    const [productCode, setProductCode] = useState("");
    const [aoiId, setAoiId] = useState("");
    const [sqMi, setSqMi] = useState("100");
    const [termStart, setTermStart] = useState(today);
    const [termEnd, setTermEnd] = useState("");
    const [contractRef, setContractRef] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [warning, setWarning] = useState(null);

    const [products, setProducts] = useState([]);
    const [aois, setAois] = useState([]);
    const [loadingRefs, setLoadingRefs] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [prodRes, aoiRes] = await Promise.all([
                    fetchWithAuth(`/api/v1/platform/analytics-products`),
                    // We use the same coverage_aois endpoint here for now.
                    // Note: analytics grants reference aiprime.aois (project AOIs),
                    // not coverage_aois. For the platform-admin UI MVP we list
                    // coverage AOIs as a stand-in until an org-scoped AOI picker
                    // is added. AOI is optional anyway (null = "any AOI in any project").
                    fetchWithAuth(`/api/v1/platform/coverage-aois`),
                ]);
                const prodData = await prodRes.json();
                const aoiData = await aoiRes.json();
                setProducts(prodData.products || []);
                setAois(aoiData.coverage_aois || []);
                if (prodData.products?.length) setProductCode(prodData.products[0].product_code);
            } catch (e) {
                console.error(e);
                setError("Failed to load reference data");
            } finally {
                setLoadingRefs(false);
            }
        })();
    }, [orgId]);

    const selectedProduct = products.find(p => p.product_code === productCode);
    const isFlat = selectedProduct?.unit === "flat";

    async function submit() {
        setSubmitting(true);
        setError(null);
        setWarning(null);
        try {
            const res = await fetchWithAuth(
                `/api/v1/platform/orgs/${orgId}/analytics-grants`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        product_code: productCode,
                        aoi_id: aoiId || null,
                        sq_mi_purchased: isFlat ? null : Number(sqMi),
                        term_start: termStart,
                        term_end: termEnd || null,
                        contract_ref: contractRef || null,
                    }),
                }
            );

            const body = await res.json();
            if (!res.ok) {
                throw new Error(body?.error || "Grant failed");
            }

            // Surface prerequisite warning if backend flagged one
            if (body.prerequisite_warning) {
                setWarning(body.prerequisite_warning);
                setSubmitting(false);
                // Grant was created — refresh parent in background, but keep
                // modal open so the user reads the warning and dismisses with Close.
                await onRefresh?.();
                return;
            }

            await onSuccess();
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
            <div className="w-[480px] bg-bg-soft border border-bg-border rounded p-4 space-y-4 max-h-[90vh] overflow-y-auto">
                <h2 className="text-sm font-semibold">Grant Analytics</h2>

                {loadingRefs ? (
                    <div className="text-xs text-gray-400">Loading…</div>
                ) : (
                    <>
                        <div>
                            <label className="block text-xs mb-1 text-gray-400">Product</label>
                            <select
                                value={productCode}
                                onChange={e => setProductCode(e.target.value)}
                                className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                            >
                                {products.map(p => (
                                    <option key={p.product_code} value={p.product_code}>
                                        {productDisplayName(p.product_code, p.display_name)}{p.is_bundle ? " (bundle)" : ""}
                                    </option>
                                ))}
                            </select>
                            {selectedProduct?.is_bundle && (
                                <p className="text-xs text-accent-info mt-1">
                                    {/* COUNTED, NOT TYPED. This read "all 13" -- true when the
                                        bundle was written and wrong by two before Portal was
                                        added, because both readers expand BUNDLE_ALL with
                                        `where is_bundle = false` evaluated at request time.
                                        A number in copy that describes a table is a number
                                        that goes stale the next time the table changes. */}
                                    This bundle includes all {products.filter(p => !p.is_bundle).length} individual products.
                                </p>
                            )}
                            {selectedProduct?.requires_imagery_sku && (
                                <p className="text-xs text-yellow-400 mt-1">
                                    ⚠ Requires imagery SKU: {selectedProduct.requires_imagery_sku}.
                                    Ensure the org has this imagery entitlement before granting.
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-xs mb-1 text-gray-400">
                                AOI <span className="text-fg-secondary">(optional — null = any AOI)</span>
                            </label>
                            <select
                                value={aoiId}
                                onChange={e => setAoiId(e.target.value)}
                                className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                            >
                                <option value="">Any AOI in any project</option>
                                {aois.map(a => (
                                    <option key={a.id} value={a.id}>
                                        {a.name} {a.basin && `(${a.basin}/${a.region})`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {!isFlat && (
                            <div>
                                <label className="block text-xs mb-1 text-gray-400">
                                    Sq Mi Purchased
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    step="any"
                                    value={sqMi}
                                    onChange={e => setSqMi(e.target.value)}
                                    className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                                />
                            </div>
                        )}
                        {isFlat && (
                            <p className="text-xs text-fg-secondary italic">
                                This is a flat-rate product — no sq mi pool. Org has access for the
                                term duration.
                            </p>
                        )}

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-xs mb-1 text-gray-400">Term Start</label>
                                <input
                                    type="date"
                                    value={termStart}
                                    onChange={e => setTermStart(e.target.value)}
                                    className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs mb-1 text-gray-400">
                                    Term End <span className="text-fg-secondary">(optional)</span>
                                </label>
                                <input
                                    type="date"
                                    value={termEnd}
                                    onChange={e => setTermEnd(e.target.value)}
                                    className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs mb-1 text-gray-400">
                                Contract Ref <span className="text-fg-secondary">(optional)</span>
                            </label>
                            <input
                                value={contractRef}
                                onChange={e => setContractRef(e.target.value)}
                                placeholder="ACME-2026-001"
                                className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                            />
                        </div>

                        {error && <div className="text-xs text-red-400">{error}</div>}
                        {warning && (
                            <div className="text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/30 rounded p-2">
                                {warning}
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={onClose}
                                className="px-3 py-1 rounded border border-bg-border text-gray-400 text-xs"
                            >
                                Close
                            </button>
                            {!warning && (
                                <button
                                    onClick={submit}
                                    disabled={submitting || !productCode}
                                    className="
                    px-3 py-1 rounded text-xs
                    bg-accent-action/20 border border-accent-action/60
                    text-accent-action
                    disabled:opacity-50
                  "
                                >
                                    {submitting ? "Granting…" : "Grant"}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────
// EditAnalyticsGrantModal
// ──────────────────────────────────────────────────────────
function EditAnalyticsGrantModal({ grant, onClose, onSuccess }) {
    const [sqMi, setSqMi] = useState(grant.sq_mi_purchased ?? "");
    const [termEnd, setTermEnd] = useState(
        grant.term_end ? grant.term_end.slice(0, 10) : ""
    );
    const [contractRef, setContractRef] = useState(grant.contract_ref || "");
    const [status, setStatus] = useState(grant.status);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    async function submit() {
        setSubmitting(true);
        setError(null);
        try {
            const res = await fetchWithAuth(
                `/api/v1/platform/analytics-grants/${grant.id}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        sq_mi_purchased: sqMi === "" ? null : Number(sqMi),
                        term_end: termEnd || null,
                        contract_ref: contractRef,
                        status,
                    }),
                }
            );
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err?.error || "Update failed");
            }
            await onSuccess();
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
            <div className="w-[440px] bg-bg-soft border border-bg-border rounded p-4 space-y-4">
                <h2 className="text-sm font-semibold">Edit Analytics Grant</h2>

                <div className="text-xs text-fg-secondary italic">
                    {productDisplayName(grant.product_code, grant.product_display_name)}
                    {grant.is_bundle && " (bundle)"}
                    <br />
                    Consumed: {Number(grant.sq_mi_consumed).toFixed(1)} sq mi
                </div>

                <div>
                    <label className="block text-xs mb-1 text-gray-400">
                        Sq Mi Purchased <span className="text-fg-secondary">(empty = unmetered)</span>
                    </label>
                    <input
                        type="number"
                        min="1"
                        step="any"
                        value={sqMi}
                        onChange={e => setSqMi(e.target.value)}
                        className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                    />
                    <p className="text-xs text-fg-secondary mt-1">
                        Must be ≥ {Number(grant.sq_mi_consumed).toFixed(1)} (current consumption).
                    </p>
                </div>

                <div>
                    <label className="block text-xs mb-1 text-gray-400">Term End</label>
                    <input
                        type="date"
                        value={termEnd}
                        onChange={e => setTermEnd(e.target.value)}
                        className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                    />
                </div>

                <div>
                    <label className="block text-xs mb-1 text-gray-400">Contract Ref</label>
                    <input
                        value={contractRef}
                        onChange={e => setContractRef(e.target.value)}
                        className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                    />
                </div>

                <div>
                    <label className="block text-xs mb-1 text-gray-400">Status</label>
                    <select
                        value={status}
                        onChange={e => setStatus(e.target.value)}
                        className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                    >
                        <option value="active">active</option>
                        <option value="suspended">suspended</option>
                        <option value="revoked">revoked</option>
                        <option value="expired">expired</option>
                    </select>
                </div>

                {error && <div className="text-xs text-red-400">{error}</div>}

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        onClick={onClose}
                        className="px-3 py-1 rounded border border-bg-border text-gray-400 text-xs"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={submitting}
                        className="
              px-3 py-1 rounded text-xs
              bg-accent-action/20 border border-accent-action/60
              text-accent-action
            "
                    >
                        {submitting ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}